import React, { useState, useEffect } from 'react';
import { 
    Card, 
    Table, 
    Input, 
    Select, 
    Space, 
    Button, 
    Tag, 
    Modal, 
    Form, 
    message,
    Popconfirm,
    Switch,
    InputNumber,
    Tooltip
} from 'antd';
import { 
    SettingOutlined,
    SearchOutlined,
    EditOutlined,
    PlusOutlined,
    DeleteOutlined,
    FilterOutlined,
    ReloadOutlined,
    SaveOutlined,
    CloseOutlined
} from '@ant-design/icons';
import { useQuery, useMutation } from '@apollo/client';
import { 
    GET_CONFIGURATION_LIST, 
    UPDATE_CONFIGURATION, 
    CREATE_CONFIGURATION,
    DELETE_CONFIGURATION 
} from '../../shared/graphql/configuration.gql';
import './Configuration.less';
import type { ColumnsType } from 'antd/es/table';
import { useIntl } from 'react-intl';   

const { Search } = Input;
const { Option } = Select;

// Combined helper to get configurations from session storage
export const getConfigurationsFromSession = (configId?: string): string | Configuration[] | null => {
    try {
        const configs = sessionStorage.getItem('app_configurations');
        if (configs) {
            const parsedConfigs = JSON.parse(configs);
            
            // If configId provided, return specific config value
            if (configId) {
                const config = parsedConfigs.find((c: any) => c.id === configId);
                return config?.value || null;
            }
            
            // If no configId, return all configurations
            return parsedConfigs;
        }
    } catch (error) {
        console.error('Error getting configuration from session:', error);
    }
    return configId ? null : [];
};

interface Configuration {
    id: string;
    name: string;
    value: string;
    inputType: 'TEXT' | 'NUMBER' | 'BOOLEAN';
    createdAt: string;
    createdBy: string;
    createdByUser?: {
        id: string;
        name: string;
        userName: string;
    };
    updatedAt: string;
    updatedBy: string;
    updatedByUser?: {
        id: string;
        name: string;
        userName: string;
    };
}

const ConfigurationManagement: React.FC = () => {
    const [searchValue, setSearchValue] = useState('');
    const [inputTypeFilter, setInputTypeFilter] = useState('ALL');
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState<Configuration | null>(null);
    const [form] = Form.useForm();
    const [createForm] = Form.useForm();
    const intl = useIntl();
    // GraphQL queries and mutations
    const { data: configData, loading: configsLoading, refetch: refetchConfigs } = useQuery(GET_CONFIGURATION_LIST, {
        variables: {
            filter: {
                search: searchValue || undefined,
                inputType: inputTypeFilter !== 'ALL' ? inputTypeFilter : undefined,
            }
        },
        errorPolicy: 'all',
        onCompleted: (data) => {
            // Save configurations to session storage when loaded
            if (data?.getConfigurationList?.success && data.getConfigurationList.configurations) {
                sessionStorage.setItem('app_configurations', JSON.stringify(data.getConfigurationList.configurations));
            }
        }
    });

    const [updateConfiguration] = useMutation(UPDATE_CONFIGURATION, {
        onCompleted: (data) => {
            if (data.updateConfiguration.success) {
                message.success(data.updateConfiguration.message);
                setEditModalVisible(false);
                // Clear session storage and refetch
                sessionStorage.removeItem('app_configurations');
                refetchConfigs();
            } else {
                message.error(data.updateConfiguration.message);
            }
        },
        onError: (error) => {
            message.error('Failed to update configuration: ' + error.message);
        }
    });

    const [createConfiguration] = useMutation(CREATE_CONFIGURATION, {
        onCompleted: (data) => {
            if (data.createConfiguration.success) {
                message.success(data.createConfiguration.message);
                setCreateModalVisible(false);
                createForm.resetFields();
                // Clear session storage and refetch
                sessionStorage.removeItem('app_configurations');
                refetchConfigs();
            } else {
                message.error(data.createConfiguration.message);
            }
        },
        onError: (error) => {
            message.error('Failed to create configuration: ' + error.message);
        }
    });

    const [deleteConfiguration] = useMutation(DELETE_CONFIGURATION, {
        onCompleted: (data) => {
            if (data.deleteConfiguration.success) {
                message.success(data.deleteConfiguration.message);
                // Clear session storage and refetch
                sessionStorage.removeItem('app_configurations');
                refetchConfigs();
            } else {
                message.error(data.deleteConfiguration.message);
            }
        },
        onError: (error) => {
            message.error('Failed to delete configuration: ' + error.message);
        }
    });

    // Debounced search effect
    useEffect(() => {
        const timer = setTimeout(() => {
            refetchConfigs();
        }, 500);

        return () => clearTimeout(timer);
    }, [searchValue, inputTypeFilter]);

    const handleEdit = (config: Configuration) => {
        setSelectedConfig(config);
        form.setFieldsValue({
            id: config.id,
            value: config.inputType === 'BOOLEAN' ? config.value === 'true' : config.value,
        });
        setEditModalVisible(true);
    };

    const handleUpdate = async (values: any) => {
        if (!selectedConfig) return;

        let finalValue = values.value;
        if (selectedConfig.inputType === 'BOOLEAN') {
            finalValue = values.value ? 'true' : 'false';
        }

        await updateConfiguration({
            variables: {
                input: {
                    id: values.id,
                    value: finalValue.toString(),
                }
            }
        });
    };

    const handleCreate = async (values: any) => {
        let finalValue = values.value;
        if (values.inputType === 'BOOLEAN') {
            finalValue = values.value ? 'true' : 'false';
        }

        await createConfiguration({
            variables: {
                input: {
                    id: values.id,
                    name: values.name,
                    value: finalValue.toString(),
                    inputType: values.inputType,
                }
            }
        });
    };

    const handleDelete = async (id: string) => {
        await deleteConfiguration({
            variables: {
                input: { id }
            }
        });
    };

    const getInputTypeTag = (inputType: string) => {
        const colors = {
            TEXT: 'blue',
            NUMBER: 'green',
            BOOLEAN: 'orange',
        };
        return <Tag color={colors[inputType as keyof typeof colors]}>{intl.formatMessage({ id: inputType })}</Tag>;
    };

    const renderValueCell = (value: string, inputType: string) => {
        switch (inputType) {
            case 'BOOLEAN':
                return (
                    <Tag color={value === 'true' ? 'green' : 'red'}>
                        {value === 'true' ? 'TRUE' : 'FALSE'}
                    </Tag>
                );
            case 'NUMBER':
                return <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>{value}</span>;
            default:
                return <span>{value}</span>;
        }
    };

    const renderEditInput = (inputType: string, value: any) => {
        switch (inputType) {
            case 'BOOLEAN':
                return (
                    <Form.Item name="value" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                );
            case 'NUMBER':
                return (
                    <Form.Item 
                        name="value" 
                        rules={[{ required: true, message: intl.formatMessage({ id: 'PLEASE_ENTER_A_NUMBER' })}]}
                    >
                        <InputNumber style={{ width: '100%' }} placeholder={intl.formatMessage({ id: 'ENTER_NUMBER_VALUE' })} />
                    </Form.Item>
                );
            default:
                return (
                    <Form.Item 
                        name="value" 
                        rules={[{ required: true, message: intl.formatMessage({ id: 'PLEASE_ENTER_A_VALUE' })}]}
                    >
                        <Input placeholder={intl.formatMessage({ id: 'ENTER_TEXT_VALUE' })} />
                    </Form.Item>
                );
        }
    };

    const columns: ColumnsType<Configuration> = [
        {
            title: intl.formatMessage({ id: 'ID' }),
            dataIndex: 'id',
            key: 'id',
            width: 200,
            render: (id) => <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{id}</span>,
        },
        {
            title: intl.formatMessage({ id: 'NAME' }),
            dataIndex: 'name',
            key: 'name',
            width: 250,
            render: (name) => <span style={{ fontWeight: 500 }}>{name}</span>,
        },
        {
            title: intl.formatMessage({ id: 'VALUE' }),
            key: 'value',
            width: 200,
            render: (_, record) => renderValueCell(record.value, record.inputType),
        },
        {
            title: intl.formatMessage({ id: 'TYPE' }),
            dataIndex: 'inputType',
            key: 'inputType',
            width: 100,
            render: (inputType) => getInputTypeTag(inputType),
        },
        {
            title: intl.formatMessage({ id: 'UPDATED_BY' }),
            key: 'updatedBy',
            width: 150,
            render: (_, record) => (
                record.updatedByUser ? (
                    <div>
                        <div style={{ fontSize: '12px' }}>{record.updatedByUser.name}</div>
                        <div style={{ fontSize: '11px', color: '#666' }}>@{record.updatedByUser.userName}</div>
                    </div>
                ) : (
                    <span style={{ color: '#ccc' }}>{intl.formatMessage({ id: 'SYSTEM' })}</span>
                )
            ),
        },
        {
            title: intl.formatMessage({ id: 'UPDATED_AT' }),
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: 120,
            render: (date) => new Date(date).toLocaleDateString(),
            sorter: true,
        },
        {
            title: intl.formatMessage({ id: 'ACTIONS' }),
            key: 'actions',
            width: 150,
            render: (_, record) => (
                <Space>
                    <Tooltip title={intl.formatMessage({ id: 'EDIT_CONFIGURATION' })}>
                        <Button 
                            type="link" 
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                        >
                            {intl.formatMessage({ id: 'EDIT' })}
                        </Button>
                    </Tooltip>
                    
                    <Popconfirm
                        title={intl.formatMessage({ id: 'DELETE_CONFIGURATION' })}
                        description={intl.formatMessage({ id: 'DELETE_CONFIGURATION_DESCRIPTION' })}
                        onConfirm={() => handleDelete(record.id)}
                        okText={intl.formatMessage({ id: 'YES' })}
                        cancelText={intl.formatMessage({ id: 'NO' })}
                        okButtonProps={{ danger: true }}
                    >
                        <Button 
                            type="link" 
                            icon={<DeleteOutlined />}
                            danger
                        >
                            {intl.formatMessage({ id: 'DELETE' })}
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const configurations = configData?.getConfigurationList?.configurations || [];
    const total = configData?.getConfigurationList?.total || 0;

    return (
        <div className="configuration-container">
            <Card>
                <div className="configuration-header">
                    <h1>
                        <SettingOutlined style={{ marginRight: '12px', color: '#1890ff' }} />
                        {intl.formatMessage({ id: 'CONFIGURATION_MANAGEMENT' })}
                    </h1>
                    
                    <Space wrap>
                        <Search
                            placeholder={intl.formatMessage({ id: 'SEARCH_CONFIGURATIONS_BY_ID_NAME_OR_VALUE' })}
                            allowClear
                            style={{ width: 300 }}
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            prefix={<SearchOutlined />}
                        />
                        
                        <Select
                            placeholder={intl.formatMessage({ id: 'TYPE' })}
                            style={{ width: 120 }}
                            value={inputTypeFilter}
                            onChange={setInputTypeFilter}
                            suffixIcon={<FilterOutlined />}
                        >
                            <Option value="ALL">{intl.formatMessage({ id: 'ALL_TYPES' })}</Option>
                            <Option value="TEXT">{intl.formatMessage({ id: 'TEXT' })}</Option>
                            <Option value="NUMBER">{intl.formatMessage({ id: 'NUMBER' })}</Option>
                            <Option value="BOOLEAN">{intl.formatMessage({ id: 'BOOLEAN' })}</Option>
                        </Select>
                        
                        <Button 
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setCreateModalVisible(true)}
                        >
                            {intl.formatMessage({ id: 'ADD_CONFIG' })}
                        </Button>
                        
                        <Button 
                            icon={<ReloadOutlined />}
                            onClick={() => refetchConfigs()}
                        >
                            {intl.formatMessage({ id: 'REFRESH' })}
                        </Button>
                    </Space>
                </div>

                <Table
                    columns={columns}
                    dataSource={configurations}
                    rowKey="id"
                    loading={configsLoading}
                    pagination={{
                        pageSize: 20,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => 
                            `${range[0]}-${range[1]} ${intl.formatMessage({ id: 'OF' })} ${total} ${intl.formatMessage({ id: 'CONFIGURATIONS' })}`,
                    }}
                    scroll={{ x: 1000 }}
                />
            </Card>

            {/* Edit Configuration Modal */}
            <Modal
                title={intl.formatMessage({ id: 'EDIT_CONFIGURATION' })}
                open={editModalVisible}
                onCancel={() => setEditModalVisible(false)}
                onOk={() => form.submit()}
                okText={intl.formatMessage({ id: 'UPDATE' })}
                width={500}
                okButtonProps={{ icon: <SaveOutlined /> }}
                cancelButtonProps={{ icon: <CloseOutlined /> }}
                cancelText={intl.formatMessage({ id: 'CANCEL' })}
            >
                {selectedConfig && (
                    <>
                        <div style={{ marginBottom: 16, padding: 16, background: '#fafafa', borderRadius: 6 }}>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <div>
                                    <strong>ID:</strong> 
                                    <span style={{ fontFamily: 'monospace', marginLeft: 8 }}>{selectedConfig.id}</span>
                                </div>
                                <div>
                                    <strong>{intl.formatMessage({ id: 'NAME' })}:</strong> {selectedConfig.name}
                                </div>
                                <div>
                                    <strong>{intl.formatMessage({ id: 'TYPE' })}:</strong> {getInputTypeTag(selectedConfig.inputType)}
                                </div>
                            </Space>
                        </div>

                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleUpdate}
                        >
                            <Form.Item name="id" hidden>
                                <Input />
                            </Form.Item>
                            
                            <Form.Item
                                label={intl.formatMessage({ id: 'VALUE' })}
                                help={intl.formatMessage({ id: `ENTER_A_${selectedConfig.inputType.toUpperCase()}_VALUE` })}
                            >
                                {renderEditInput(selectedConfig.inputType, selectedConfig.value)}
                            </Form.Item>
                        </Form>
                    </>
                )}
            </Modal>

            {/* Create Configuration Modal */}
            <Modal
                title={intl.formatMessage({ id: 'CREATE_NEW_CONFIGURATION' })}
                open={createModalVisible}
                onCancel={() => setCreateModalVisible(false)}
                onOk={() => createForm.submit()}
                okText={intl.formatMessage({ id: 'CREATE' })}
                width={600}
                okButtonProps={{ icon: <SaveOutlined /> }}
                cancelButtonProps={{ icon: <CloseOutlined /> }}
                cancelText={intl.formatMessage({ id: 'CANCEL' })}
            >
                <Form
                    form={createForm}
                    layout="vertical"
                    onFinish={handleCreate}
                >
                    <Form.Item
                        name="id"
                        label={intl.formatMessage({ id: 'ID' })}
                        rules={[
                            { required: true, message: intl.formatMessage({ id: 'PLEASE_ENTER_CONFIGURATION_ID' }) },
                            { pattern: /^[a-z0-9-]+$/, message: 'ID can only contain lowercase letters, numbers, and hyphens' }
                        ]}
                        help={intl.formatMessage({ id: 'UNIQUE_IDENTIFIER_LOWERCASE_LETTERS_NUMBERS_AND_HYPHENS_ONLY' })}
                    >
                        <Input placeholder={intl.formatMessage({ id: 'ENTER_CONFIGURATION_ID' })} />
                    </Form.Item>
                    
                    <Form.Item
                        name="name"
                        label={intl.formatMessage({ id: 'NAME' })}
                        rules={[{ required: true, message: intl.formatMessage({ id: 'PLEASE_ENTER_CONFIGURATION_NAME' })}]}
                        help={intl.formatMessage({ id: 'HUMAN_READABLE_NAME_FOR_THIS_CONFIGURATION' })}
                    >
                        <Input placeholder={intl.formatMessage({ id: 'ENTER_CONFIGURATION_NAME' })} />
                    </Form.Item>
                    
                    <Form.Item
                        name="inputType"
                        label={intl.formatMessage({ id: 'TYPE' })}
                        rules={[{ required: true, message: intl.formatMessage({ id: 'PLEASE_SELECT_INPUT_TYPE' })}]}
                        help={intl.formatMessage({ id: 'DATA_TYPE_FOR_THIS_CONFIGURATION_VALUE' })}
                    >
                        <Select placeholder="Select data type">
                            <Option value="TEXT">{intl.formatMessage({ id: 'TEXT' })}</Option>
                            <Option value="NUMBER">{intl.formatMessage({ id: 'NUMBER' })}</Option>
                            <Option value="BOOLEAN">{intl.formatMessage({ id: 'BOOLEAN' })}</Option>
                        </Select>
                    </Form.Item>
                    
                    <Form.Item
                        name="value"
                        label={intl.formatMessage({ id: 'INITIAL_VALUE' })}
                        rules={[{ required: true, message: intl.formatMessage({ id: 'PLEASE_ENTER_INITIAL_VALUE' })}]}
                        help={intl.formatMessage({ id: 'THE_DEFAULT_VALUE_FOR_THIS_CONFIGURATION' })}
                    >
                        <Input placeholder="Enter initial value" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ConfigurationManagement;